import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect } from 'react';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';

import { Order } from '@rebound/shared-models/order/order.model';

import OrderLookupDetails from '@modules/dashboard/components/OrderLookupDetails';
import { useRetailerData } from '@modules/retailer/retailer.context';
import { anyItemWithinReturnWindow } from '@modules/return-journey/return-journey.utils';

type OrderDetailsModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  order: Order;
  setFoundOrder: React.Dispatch<React.SetStateAction<Order | null>>;
};

const OrderDetailsModal = ({
  visible,
  setVisible,
  order,
  setFoundOrder,
}: OrderDetailsModalProps) => {
  const retailerData = useRetailerData();

  const closeModal = useCallback(() => {
    setVisible(false);
    setFoundOrder(null);
  }, [setFoundOrder, setVisible]);

  useEffect(() => {
    if (visible) {
      const closeOnEsc = (e: KeyboardEvent) => {
        if (e.keyCode === 27) {
          closeModal();
        }
      };
      window.addEventListener('keydown', closeOnEsc);

      return () => {
        window.removeEventListener('keydown', closeOnEsc);
      };
    }
  }, [visible, closeModal]);

  const retailer = retailerData.retailer;

  const returnAvailable =
    retailer &&
    !!order?.items.find((i) => !i.nonReturnable) &&
    (anyItemWithinReturnWindow(order, retailer.returnPolicyDays) ||
      Boolean(order.rma));

  return (
    <>
      {visible && (
        <Wrapper visible={visible} data-testid="order-details-modal">
          <ContentWrapper>
            <OrderLookupDetails
              order={order}
              closeModal={closeModal}
              retailer={retailer}
              returnAvailable={returnAvailable}
            />
            <CloseIcon
              onClick={() => closeModal()}
              data-cy="close-details-modal-button"
            >
              <XMarkIcon height="20" />
            </CloseIcon>
          </ContentWrapper>
        </Wrapper>
      )}
    </>
  );
};

const Wrapper = styled.div<{ visible: boolean }>`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  z-index: 25;
  overflow-y: auto;

  ${down('xs')} {
    padding: 8px;
  }

  ${up('lg')} {
    padding: 0 140px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding: 24px 8px;
  background-color: ${(props) => props.theme.colors.white};
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  margin: auto;

  ${down('xs')} {
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0px 10px 10px -5px rgba(0, 0, 0, 0.04);
    border-radius: 8px;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  margin: 20px 8px;
  cursor: pointer;

  ${up('sm')} {
    margin: 24px;
  }
`;

export default OrderDetailsModal;
